import { useState } from "react";
import { Tag, X, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem } from "@/components/ui/dropdown-menu";
import { CONVERSATIONS } from "@/constants/testIds";
import { CONVERSATION_STAGES, CONVERSATION_TAGS, tagLabel } from "@/lib/conversationsConstants";

const SELECT_CLASS = "h-8 rounded-md border border-border bg-white px-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60";
// Fix AI (2026-10-23): en el celular los campos son mas altos (para el dedo)
const SELECT_TOUCH = "h-10 w-3/5 rounded-md border border-border bg-white px-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60";

// Fix AL (2026-10-23): marca cuando la etapa o el servicio los puso el agente de IA
const AiMark = ({ title, testId }) => (
  <span title={title} className="rounded-sm bg-brand-light px-1 text-[9px] font-semibold uppercase tracking-wide text-brand" data-testid={testId}>IA</span>
);

/** Fix AD (2026-10-15): etapa comercial, servicio de interes y etiquetas del
 * chat, debajo del encabezado de la conversacion. */
export default function ConversationClassification({ conversation, services, onClassify, tagList = CONVERSATION_TAGS, stageList = CONVERSATION_STAGES, compact = false }) {
  const [saving, setSaving] = useState(false);
  // Fix AI (2026-10-23): en el celular la etapa, el servicio y las etiquetas quedan plegados (el chat necesita el alto)
  const [open, setOpen] = useState(false);
  const tags = conversation.tags || [];
  // Fix AE (2026-10-19): etiquetas de la clinica; las ocultas solo se ven si el chat ya las tiene
  const tagOptions = tagList.filter((t) => !t.hidden || tags.includes(t.key));
  const serviceOptions = services.filter((s) => s.is_active !== false || s.id === conversation.interest_service_id);
  // Fix AG (2026-10-21): etapas de la clinica; las ocultas solo se ven si el chat ya la tiene
  const stageOptions = stageList.filter((s) => !s.hidden || s.key === (conversation.stage || "nuevo"));

  const save = async (changes) => {
    setSaving(true);
    try {
      await onClassify(changes);
    } finally {
      setSaving(false);
    }
  };
  const toggleTag = (key) => save({ tags: tags.includes(key) ? tags.filter((t) => t !== key) : [...tags, key] });

  const stageName = (stageOptions.find((s) => s.key === (conversation.stage || "nuevo")) || {}).label || "Nuevo";
  const labelClass = `flex items-center gap-1.5 text-muted-foreground ${compact ? "w-full justify-between text-sm" : "text-xs"}`;

  return (
    <>
      {compact && (
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex items-center justify-between gap-2 px-3 h-11 shrink-0 border-b border-border bg-muted/30 text-sm text-foreground [@media(max-height:499px)]:group-has-[textarea:focus]:hidden" data-testid={CONVERSATIONS.classificationToggle}>
          <span className="truncate">Etapa: {stageName}{tags.length > 0 ? ` · ${tags.length} ${tags.length === 1 ? "etiqueta" : "etiquetas"}` : ""}</span>
          <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      )}
    <div className={`${compact ? "px-3" : "px-5"} py-2.5 border-b border-border flex items-center gap-x-4 gap-y-2 flex-wrap bg-muted/30 ${compact ? (open ? "max-h-[28dvh] overflow-y-auto shrink-0 [@media(max-height:499px)]:group-has-[textarea:focus]:hidden" : "hidden") : ""}`} data-testid={CONVERSATIONS.classificationBar}>
      <label className={labelClass}>
        Etapa
        <select className={compact ? SELECT_TOUCH : SELECT_CLASS} value={conversation.stage || "nuevo"} disabled={saving} onChange={(e) => save({ stage: e.target.value })} data-testid={CONVERSATIONS.stageSelect}>
          {stageOptions.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        {conversation.stage_by_agent && <AiMark title="La etapa la puso el agente de IA" testId={CONVERSATIONS.stageAiMark} />}
      </label>
      <label className={labelClass}>
        Servicio de interés
        <select className={compact ? SELECT_TOUCH : `${SELECT_CLASS} max-w-[180px]`} value={conversation.interest_service_id || ""} disabled={saving} onChange={(e) => save({ interest_service_id: e.target.value })} data-testid={CONVERSATIONS.serviceSelect}>
          <option value="">Sin servicio</option>
          {serviceOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {conversation.interest_by_agent && conversation.interest_service_id && <AiMark title="El servicio lo puso el agente de IA" testId={CONVERSATIONS.serviceAiMark} />}
      </label>
      {/* Fix AN (2026-10-23): la paciente pidio no recibir mensajes automaticos (escribio BAJA o STOP) */}
      {conversation.auto_opt_out && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid={CONVERSATIONS.autoOptOutChip}>
          <span className="inline-flex items-center rounded-full border border-[#B5793A]/40 bg-[#F4E4D6] px-2 py-0.5 text-[11px] text-[#B5793A]">No quiere mensajes automáticos</span>
          <button type="button" onClick={() => save({ auto_opt_out: false })} disabled={saving} className={`rounded-full border border-border bg-white px-2 ${compact ? "py-2 text-xs" : "py-0.5 text-[11px]"} text-muted-foreground hover:text-foreground disabled:opacity-60`} data-testid={CONVERSATIONS.autoOptOutAllowButton}>
            Volver a permitir
          </button>
        </div>
      )}
      <div className="flex items-center gap-1.5 flex-wrap">
        {tags.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-2 py-0.5 text-[11px] text-foreground" data-testid={`${CONVERSATIONS.tagChipPrefix}${t}`}>
            {tagLabel(t, tagList)}
            <button type="button" onClick={() => toggleTag(t)} disabled={saving} className="text-muted-foreground hover:text-foreground" aria-label={`Quitar ${tagLabel(t, tagList)}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" disabled={saving} className={`inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 ${compact ? "py-2 text-xs" : "py-0.5 text-[11px]"} text-muted-foreground hover:text-foreground`} data-testid={CONVERSATIONS.tagsMenuButton}>
              <Tag className="w-3 h-3" /> Etiquetas
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            {tagOptions.map((t) => (
              <DropdownMenuCheckboxItem
                key={t.key}
                checked={tags.includes(t.key)}
                disabled={saving}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={() => toggleTag(t.key)}
                data-testid={`${CONVERSATIONS.tagOptionPrefix}${t.key}`}
              >
                {t.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
    </>
  );
}
